import type {FC} from 'react';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
// web's ButtonBase/IconButton don't fit here: ButtonBase has no web wrapper, and
// web's IconButton takes an icon-name string rather than an Icon child
import {ButtonBase, IconButton} from '@mui/material';
import {useQuery} from '@tanstack/react-query';
import {Theme} from '@wellstone-solutions/common';
import {
  Box,
  Chip,
  Icon,
  Menu,
  MenuItem,
  Typography,
} from '@wellstone-solutions/web';
import {InitialsAvatar} from 'components/InitialsAvatar';
import {LD_FLAGS, useFeatureFlag} from 'modules/feature-flags';
import {toPatient, toSignal} from '../../adapters';
import {kindsQueryOptions} from '../../queries';
import {signalIconName} from '../../signalIcons';
import {formatRelativeTime} from '../../relativeTime';
import type {MemberIndicatorGroup} from '../../types';
import {SignalCard} from '../SignalCard';
import {KIND_CELL, RAISED_CELL, ROW_GRID} from './layout';

function latestOccurredAt(occurredAts: string[]): string | null {
  if (occurredAts.length === 0) return null;
  return occurredAts.reduce((latest, value) =>
    Date.parse(value) > Date.parse(latest) ? value : latest,
  );
}

type SignalRowProps = {
  group: MemberIndicatorGroup;
  striped: boolean;
  // Absent while no feedback form is active
  onGiveFeedback?: (indicatorId: string) => void;
};

export const SignalRow: FC<SignalRowProps> = ({
  group,
  striped,
  onGiveFeedback,
}) => {
  const [open, setOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const canCreateNote = useFeatureFlag(LD_FLAGS.CONTACT_NOTES);
  const kindsQuery = useQuery(kindsQueryOptions());
  const patient = toPatient(group.member);
  const firstName = patient.name.trim().split(/\s+/)[0] ?? '';
  const closeMenu = () => setMenuAnchor(null);
  const goTo = (path: string) => {
    closeMenu();
    navigate(path);
  };
  const signals = group.indicators.map(toSignal);
  const raisedAt = latestOccurredAt(signals.map((signal) => signal.occurredAt));
  const kindLabels = new Map(
    (kindsQuery.data ?? []).map((kind) => [kind.slug, kind.label]),
  );
  const kinds = [
    ...new Set(group.indicators.map((indicator) => indicator.kind)),
  ];

  return (
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: striped ? 'grey.50' : 'background.paper',
      }}>
      {/* Relative wrapper so the actions button overlays the row's last grid
          column without nesting a button inside the row's ButtonBase */}
      <Box sx={{position: 'relative'}}>
        {/* No aria-label: the visible row content is the accessible name */}
        <ButtonBase
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
          sx={{
            ...ROW_GRID,
            width: '100%',
            alignItems: 'center',
            textAlign: 'left',
            py: 1.75,
            '&:hover': {bgcolor: 'action.hover'},
          }}>
          <Box
            sx={{
              display: 'flex',
              color: 'text.secondary',
              transition: 'transform 150ms',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}>
            <Icon name="NavArrowDown" size={20} />
          </Box>

          <InitialsAvatar
            aria-hidden
            name={patient.name}
            sx={{
              width: {xs: 34, md: 38},
              height: {xs: 34, md: 38},
              bgcolor: 'primary.main',
              color: 'common.white',
              fontSize: 13,
            }}
          />

          <Box
            sx={{
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}>
            <Typography sx={{fontWeight: 600, fontSize: 15}}>
              {patient.name}
            </Typography>
            {patient.programs.map((program) => (
              <Chip key={program.id} label={program.name} size="small" />
            ))}
          </Box>

          <Box
            sx={{
              ...KIND_CELL,
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 1,
              minWidth: 0,
            }}>
            {kinds.map((slug) => (
              <Box
                key={slug}
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  fontSize: 13,
                }}>
                <Icon
                  name={signalIconName(slug)}
                  size={18}
                  color={Theme.colorPalette.primary}
                />
                {kindLabels.get(slug) ?? slug}
              </Box>
            ))}
          </Box>

          <Typography sx={{fontWeight: 700, fontSize: 19}}>
            {signals.length}
          </Typography>

          <Typography
            sx={{fontSize: 13, color: 'text.secondary', ...RAISED_CELL}}>
            {raisedAt ? formatRelativeTime(raisedAt) : ''}
          </Typography>
        </ButtonBase>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: {xs: '6px', md: '20px'},
            width: {xs: '40px', md: '44px'},
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <IconButton
            size="small"
            aria-label={`Actions for ${firstName}`}
            aria-haspopup="true"
            onClick={(event) => {
              event.stopPropagation();
              setMenuAnchor(event.currentTarget);
            }}>
            <Icon name="MoreVert" size={20} />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
            transformOrigin={{horizontal: 'right', vertical: 'top'}}>
            <MenuItem onClick={() => goTo(`/members/${group.member.id}`)}>
              View {firstName}'s profile
            </MenuItem>
            {canCreateNote && (
              <MenuItem
                onClick={() =>
                  goTo(`/documentation/notes/new?patient=${group.member.id}`)
                }>
                Create note
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Box>

      {open && (
        <Box
          sx={{
            bgcolor: 'grey.50',
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 2.5,
            pb: 2,
            pl: {xs: 2.5, md: 12.5},
          }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fit, minmax(min(100%, 460px), 1fr))',
              columnGap: 5,
            }}>
            {group.indicators.map((indicator) => (
              <Box key={indicator.id} sx={{pt: 2}}>
                <SignalCard
                  indicator={indicator}
                  onGiveFeedback={onGiveFeedback}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
